import type { FocusEventHandler } from 'react';
import type {
	FieldPath,
	FieldPathValue,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form';

import { isShowIfFunction, type ShowIfFunction } from './isShowIfFunction';

import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import {
	type TextFieldProps,
	TextField as FriskTextField,
} from '@hdir/ui/text-field';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useFormContext } from './FormContext';

type MyTextFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<TextFieldProps,
	| 'defaultValue'
	| 'error'
	| 'name'
	| 'onBlur'
	| 'onChange'
	| 'ref'
	| 'value'
> & {
	errorSummaryLabel?: string;
	name: TName;
	onBlur?: FocusEventHandler<HTMLInputElement|HTMLLabelElement> & FocusEventHandler<HTMLLabelElement>;
	rules?: UseControllerProps<TFieldValues, TName>['rules'];
	showIf?: ShowIfFunction<TFieldValues>;
};


export const TextField = forwardRef(<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		autoComplete = 'off',
		// @ts-expect-error defaultValue is Omittted in the type, but one could still pass it...
		defaultValue: _unused, // Make sure defaultValue doesn't end up in otherProps.
		disabled: disabledProp,
		// @ts-expect-error error is Omittted in the type, but one could still pass it...
		error: _hardcoded_inside1, // Make sure error doesn't end up in otherProps.
		label,
		errorSummaryLabel = isStringLiteral(label) ? label : undefined,
		name,
		onBlur: onBlurProp,
		// @ts-expect-error nChange is Omittted in the type, but one could still pass it...
		onChange: _gotten_from_usecontroller1, // Make sure onChange doesn't end up in otherProps.
		rules,
		showIf,
		type,
		// @ts-expect-error value is Omittted in the type, but one could still pass it...
		value: _gotten_from_usecontroller2, // Make sure value doesn't end up in otherProps.
		...otherProps
	}: MyTextFieldProps<TFieldValues, TName>,
	refFromParent: React.ForwardedRef<HTMLInputElement>,
): React.JSX.Element | null => {
	const {
		control,
		getRequired,
		setErrorSummaryLabel,
		setFocusRef,
		setScrollRef,
	} = useFormContext<TFieldValues>();

	const required: boolean = rules
		? !!rules.required
		: getRequired
			? getRequired(name)
			: false;
	const {
		field: {
			disabled: useControllerDisabled,
			onBlur: useControllerOnBlur,
			onChange: useControllerOnChange,
			ref: useControllerRef,

			// NOTE: Setting default value to '' to avoid:
			// <<
			// Warning: A component is changing an uncontrolled input to be controlled.
			// This is likely caused by the value changing from
			// undefined to a defined value, which should not happen.
			// Decide between using a controlled or uncontrolled
			// input element for the lifetime of the component.
			// >>
			value = '',
		},
		fieldState: {
			error,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
		rules,
	});

	const focusRef = useRef<HTMLInputElement>();
	const scrollRef = useRef<HTMLDivElement>();

	const [hydrated, setHydrated] = useState(false);

	const disabled = !hydrated || useControllerDisabled;

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		setHydrated(true);
	}, []);

	useEffect(() => {
		if (focusRef.current) {
			setFocusRef({ field: name, el: focusRef.current });
		}
	}, [focusRef.current]);

	useEffect(() => {
		if (scrollRef.current) {
			setScrollRef({ field: name, el: scrollRef.current });
		}
	}, [scrollRef.current]);

	if (isShowIfFunction(showIf) && !showIf({
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		watch: (watchParams) => useWatch({...watchParams, control}) as FieldPathValue<TFieldValues, TName>,
	})) {
		return null;
	}

	return (
		<div
			ref={(el) => {
				if (el) {
					scrollRef.current = el;
				}
			}}
		>
			<FriskTextField
				{...otherProps}
				autoComplete={autoComplete}
				disabled={disabled}
				error={error ? error.message : null}
				label={label}
				name={name}

				onBlur={(e) => {
					// https://react-hook-form.com/docs/usecontroller
					// A function which sends the input's onBlur event to the library.
					// It should be assigned to the input's onBlur prop.
					useControllerOnBlur();

					if (onBlurProp) onBlurProp(e);
				}}

				onChange={(event) => {
					if (type === 'number') {
						const maybeNumber = event.target.value ? parseInt(event.target.value, 10) : undefined;
						useControllerOnChange(maybeNumber);
					} else {
						useControllerOnChange(event.target.value);
					}
				}}

				ref={(htmlInputElementOrNull) => {
					if (htmlInputElementOrNull) {
						if (typeof refFromParent === 'function') {
							refFromParent(htmlInputElementOrNull);
						} else if (refFromParent) {
							refFromParent.current = htmlInputElementOrNull;
						}
						focusRef.current = htmlInputElementOrNull;
						useControllerRef(htmlInputElementOrNull);
					}
				}}
				required={required}
				type={type}
				value={value}
			/>
		</div>
	);
});
